import { zodResolver } from '@hookform/resolvers/zod';
import _ from 'lodash';
import {
  useCallback, useEffect, useState,
} from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import {
  getAllDepartment,
} from 'app/apis/DepartmentServices';
import { updateOneUser } from 'app/apis/UserServices';
import {
  USER_DASHBOARD_PERMISSION_SCHEMA,
  UserDashBoardPermissionSchemaType,
} from 'app/constants/User';
import { OptionItem } from 'app/models/Common';
import {
  DepartmentResponse, DepartmentTypeEnum,
} from 'app/models/Department';
import { UserResponse } from 'app/models/User';
import { getImageURL } from 'app/utils';
import cn from 'app/utils/ClassName';

import Button from '../Button';
import { Form } from '../Form';
import { Image } from '../Image';
import { SortList } from '../Page';
import { Select } from '../Select';

const TABLE_SELECT_VALUES = [
  {
    label: DepartmentTypeEnum.group,
    value: DepartmentTypeEnum.group,
  },
  {
    label: DepartmentTypeEnum.family,
    value: DepartmentTypeEnum.family,
  },
  {
    label: DepartmentTypeEnum.district,
    value: DepartmentTypeEnum.district,
  },
];

interface UserDashboardPermissionTabItemProps {
  user: UserResponse;
}

export const UserDashboardPermissionTabItem = ({
  user,
}: UserDashboardPermissionTabItemProps) => {
  const [groupList, setGroupList] = useState<DepartmentResponse[]>([]);
  const [familyList, setFamilyList] = useState<DepartmentResponse[]>([]);
  const [districtList, setDistrictList] = useState<DepartmentResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedTable, setSelectedTable] = useState<OptionItem>({
    label: DepartmentTypeEnum.group,
    value: DepartmentTypeEnum.group,
  });

  const methods = useForm<UserDashBoardPermissionSchemaType>({
    defaultValues: {
      DashboardPermissions: user.DashboardPermissions,
    },
    resolver: zodResolver(USER_DASHBOARD_PERMISSION_SCHEMA),
  });

  const {
    control, reset, handleSubmit, formState,
  } = methods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'DashboardPermissions',
  });

  const sortDepartmentList = useCallback((list: DepartmentResponse[]):DepartmentResponse[] => {
    const departmentIdSet = new Set(
      user.DashboardPermissions.map((perm) => perm.departmentId),
    );
    return (_.sortBy(list, [
      (item) => !departmentIdSet.has(item.departmentId),
      (item) => item.name.toLowerCase(),
    ]));
  }, [user.DashboardPermissions]);

  const getDepartmentListData = useCallback(
    async () => {
      try {
        setIsLoading(true);
        const [group, family, district] = await Promise.all([getAllDepartment({ departmentType: 'group' }),
          getAllDepartment({ departmentType: 'family' }),
          getAllDepartment({ departmentType: 'district' }),
        ]);
        setGroupList(sortDepartmentList(group));
        setFamilyList(sortDepartmentList(family));
        setDistrictList(sortDepartmentList(district));

        if (user.DashboardPermissions.length > 0 && fields.length > 0) {
          const lists = [group, family, district];
          lists.forEach((list, index) => {
            if (!list.some((item) => item.departmentId === fields[0].departmentId)) {
              return;
            }
            setSelectedTable(TABLE_SELECT_VALUES[index]);
          });
        }
      } catch (error) {
        toast.error('未能獲取部門列表');
      } finally {
        setIsLoading(false);
      }
    },
    [fields, sortDepartmentList, user.DashboardPermissions.length],
  );

  const getIsChecked = useCallback(
    (item: DepartmentResponse) => fields.some((field) => field.departmentId === item.departmentId),
    [fields],
  );

  const handleCheckboxOnClick = useCallback(
    (item: DepartmentResponse) => {
      const index = fields.findIndex(
        (department) => department.departmentId === item.departmentId,
      );
      if (index !== -1) {
        remove(index);
        return;
      }

      append({
        departmentId: item.departmentId,
      });
    },
    [append, fields, remove],
  );

  const handleCustomListItem = useCallback(
    (item: DepartmentResponse) => (
      <div className="flex flex-1">
        <label
          htmlFor={`department-${item.departmentId}`}
          className="flex flex-1 gap-[8px]"
        >
          <input
            id={`department-${item.departmentId}`}
            type="checkbox"
            checked={getIsChecked(item)}
            onChange={() => {
              handleCheckboxOnClick(item);
            }}
          />
          <div className="flex flex-1 flex-col gap-[4px]">
            <div
              className={cn('mt-px break-all', 'text-text-primary')}
            >
              {item.name}
            </div>
            {item.Shop ? (
              <div className="flex gap-[4px]">
                <Image
                  src={getImageURL(item.Shop?.shopProfilePic ?? '')}
                  alt={item.Shop?.shopTitle}
                  className="size-[24px] shrink-0 rounded-full"
                />
                <div className="line-clamp-1 whitespace-pre-line break-all">
                  {item.Shop?.shopTitle}
                </div>
              </div>
            ) : null}
          </div>
        </label>
      </div>
    ),
    [getIsChecked, handleCheckboxOnClick],
  );

  const onSubmit = async (value: UserDashBoardPermissionSchemaType) => {
    setIsSubmitting(true);
    try {
      await updateOneUser(user.userId, {
        DashboardPermissions: value.DashboardPermissions,
      });
      toast.success('編輯會員成功');
    } catch {
      toast.error('編輯會員失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  // On Init
  useEffect(() => {
    getDepartmentListData();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="size-full overflow-y-auto">
      <Form methods={methods} innerClassName="non-mobile:grid-cols-1">
        <div className="flex justify-between px-[16px]">
          <Select
            value={selectedTable.value}
            options={TABLE_SELECT_VALUES}
            className="w-fit py-[8px]"
            onValueChange={(item) => {
              if (item) {
                setSelectedTable(item);
                reset({ DashboardPermissions: [] });
              }
            }}
          />
          <Button
            type="submit"
            variant="contained"
            color="primary"
            containerStyle="py-[8px] px-[71px] justify-center flex items-center"
            onClick={handleSubmit(onSubmit)}
            loading={isSubmitting}
            disabled={_.isEmpty(formState.dirtyFields)}
          >
            儲存
          </Button>

        </div>
        {isLoading ? 'Loading...' : (
          <div className="grid w-full grid-cols-1 gap-[16px] overflow-hidden p-[16px]">
            {selectedTable.value === DepartmentTypeEnum.group && (
            <div className="h-full flex-1 overflow-hidden rounded-[8px] border border-border-brand">
              <SortList
                type="departmentGroup"
                listItems={groupList}
                isChanged={false}
                onUpdateOrder={() => {}}
                title={`${DepartmentTypeEnum.group} 列表`}
                CustomListItemComponent={({ item }) => handleCustomListItem(item)}
                setNewOrder={() => {}}
                isDisableDrag
                containerClassName="pb-[0px]"
              />
            </div>
            )}
            {selectedTable.value === DepartmentTypeEnum.family && (
            <div className="h-full flex-1 overflow-hidden rounded-[8px] border border-border-brand">
              <SortList
                type="departmentFamily"
                listItems={familyList}
                isChanged={false}
                title={`${DepartmentTypeEnum.family} 列表`}
                onUpdateOrder={() => {}}
                CustomListItemComponent={({ item }) => handleCustomListItem(item)}
                setNewOrder={() => {}}
                isDisableDrag
                containerClassName="pb-[0px]"
              />
            </div>
            )}
            {selectedTable.value === DepartmentTypeEnum.district && (
            <div className="col-span-1 flex-1 overflow-hidden rounded-[8px] border border-border-brand">
              <SortList
                type="departmentDistrict"
                listItems={districtList}
                isChanged={false}
                title={`${DepartmentTypeEnum.district} 列表`}
                onUpdateOrder={() => {}}
                setNewOrder={() => {}}
                CustomListItemComponent={({ item }) => handleCustomListItem(item)}
                isDisableDrag
                containerClassName="pb-[0px]"
              />
            </div>
            )}
          </div>
        )}
      </Form>
    </div>
  );
};
